// Analytics initialization for Google Tag Manager and Smartlook
// This runs as a separate module to access import.meta.env

import Smartlook from 'smartlook-client';

const isDev = import.meta.env.DEV;

// Development logging helper - uses warn to comply with ESLint no-console rule
const logAnalytics = (message: string, data?: unknown) => {
  if (isDev) {
    // eslint-disable-next-line no-console
    console.log(`[Analytics] ${message}`, data ?? '');
  }
};

// Type definitions for global analytics
interface DataLayerEvent {
  event: string;
  [key: string]: string | number | null | undefined;
}

interface WindowWithAnalytics extends Window {
  dataLayer: DataLayerEvent[];
}

declare const window: WindowWithAnalytics;

// Google Tag Manager
const GTM_ID = import.meta.env.VITE_APP_GTM_ID;
logAnalytics('GTM ID:', GTM_ID || '(not configured)');

if (GTM_ID) {
  logAnalytics('Initializing Google Tag Manager...');
  // Initialize GTM dataLayer
  (function (w: WindowWithAnalytics, d: Document, s: string, l: string, i: string) {
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    const firstScript = d.getElementsByTagName(s)[0];
    const gtmScript = d.createElement(s) as HTMLScriptElement;
    const dataLayerParam = l !== 'dataLayer' ? `&l=${l}` : '';
    gtmScript.async = true;
    gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${i}${dataLayerParam}`;
    firstScript.parentNode!.insertBefore(gtmScript, firstScript);
  })(window, document, 'script', 'dataLayer', GTM_ID);

  // Track button and link clicks
  document.removeEventListener('click', sendAnalyticsForClicks);
  document.addEventListener('click', sendAnalyticsForClicks);

  function sendAnalyticsForClicks(e: Event) {
    const target = e.target as HTMLElement;
    if (target.tagName === 'BUTTON') {
      window.dataLayer.push({
        event: 'ButtonClick',
        ButtonText: target.textContent,
      });
    }
    if (target.tagName === 'A') {
      window.dataLayer.push({
        event: 'LinkClick',
        LinkText: target.textContent,
      });
    }
  }

  // GTM noscript iframe
  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_ID}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);
  logAnalytics('GTM initialized successfully', { gtmId: GTM_ID, dataLayer: window.dataLayer });
} else {
  logAnalytics('GTM skipped - no GTM_ID configured');
}

// Smartlook
const SMARTLOOK_ID = import.meta.env.VITE_APP_SMARTLOOK_ID;
logAnalytics('Smartlook ID:', SMARTLOOK_ID || '(not configured)');

if (SMARTLOOK_ID) {
  logAnalytics('Initializing Smartlook...');
  Smartlook.init(SMARTLOOK_ID, { region: 'eu' });
  logAnalytics('Smartlook initialized successfully', { smartlookId: SMARTLOOK_ID });
} else {
  logAnalytics('Smartlook skipped - no SMARTLOOK_ID configured');
}
